import { useTranslation } from 'react-i18next'
import { Alert, EmptyState, SkeletonCard, SkeletonTable } from '@/components/ui'
import { useSubscription } from '@/hooks/useSubscription'
import { SubscriptionTierCard } from './SubscriptionTierCard'
import { SubscriptionPricingTable } from './SubscriptionPricingTable'
import { SubscriptionInvoiceList } from './SubscriptionInvoiceList'

/**
 * EP-144 T144-008: the owner-only Settings Subscription tab (Q-9).
 *
 * A container rather than three hook calls in SettingsPage: the endpoint 403s
 * for every non-owner role, so the query has to be scoped to the moment the
 * tab is actually open, and the shared loading/error states belong to the one
 * request that feeds both the tier card and the pricing table. Read-only: no
 * canEdit, no Save.
 */
/**
 * The status the API answers with when the account has no subscription row at
 * all. Read off the axios error rather than the message, which is server copy.
 */
function errorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status
}

export function SubscriptionTab() {
  const { t } = useTranslation('settings')
  const { data: subscription, isLoading, isError, error } = useSubscription()

  if (isLoading) {
    return (
      <>
        <SkeletonCard />
        <div className="mt-6">
          <SkeletonTable rows={3} />
        </div>
      </>
    )
  }

  // Owner ruling 2026-09-08: there is no backfill, so an account carries no
  // subscription row until an admin saves one and the endpoint answers 404.
  // That is a state of the account, not a failed request, so it reads as an
  // empty state telling the owner who to ask. Every other status stays an error.
  if (isError && errorStatus(error) === 404) {
    return (
      <EmptyState
        title={t('subscription.none.title')}
        description={t('subscription.none.description')}
      />
    )
  }

  if (isError || !subscription) {
    return <Alert variant="error">{t('subscription.errors.load_failed')}</Alert>
  }

  return (
    <>
      <SubscriptionTierCard subscription={subscription} />
      <SubscriptionPricingTable
        pricing={subscription.pricing}
        tier={subscription.tier}
        roomCount={subscription.room_count}
      />
      <SubscriptionInvoiceList />
    </>
  )
}
